// Seeded RNG for the deterministic sim (AGENTS.md hard rule #1).
// - Pcg32: reference PCG-XSH-RR 32-bit generator (64-bit state via BigInt).
//   One instance per system ("stream") so systems don't perturb each other.
// - hash2/hash3: stateless integer hashes for per-coordinate noise (mapgen,
//   cosmetic per-tile variation). murmur3-finalizer based, Math.imul only.

const MUL = 6364136223846793005n;
const MASK64 = (1n << 64n) - 1n;

export class Pcg32 {
  private state: bigint;
  private readonly inc: bigint;

  constructor(initstate: number | bigint, initseq: number | bigint) {
    this.state = 0n;
    this.inc = ((BigInt(initseq) << 1n) | 1n) & MASK64;
    this.nextU32();
    this.state = (this.state + BigInt(initstate)) & MASK64;
    this.nextU32();
  }

  /** uniform u32 */
  nextU32(): number {
    const old = this.state;
    this.state = (old * MUL + this.inc) & MASK64;
    const xorshifted = Number((((old >> 18n) ^ old) >> 27n) & 0xffffffffn);
    const rot = Number(old >> 59n);
    return ((xorshifted >>> rot) | (xorshifted << (-rot & 31))) >>> 0;
  }

  /** uniform integer in [0, bound) — multiply-shift, no float division */
  nextInt(bound: number): number {
    return Number((BigInt(this.nextU32()) * BigInt(bound)) >> 32n);
  }

  /** Q16.16 fixed in [0, FP_ONE) */
  nextFp(): number {
    return this.nextU32() >>> 16;
  }

  /** fold internal state into two u32s (for checksumming) */
  stateWords(): [number, number] {
    return [Number(this.state & 0xffffffffn), Number((this.state >> 32n) & 0xffffffffn)];
  }
}

/** murmur3 finalizer */
function fmix(h: number): number {
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** stateless hash of (x, y, seed) -> u32. Deterministic; safe for negative coords. */
export function hash2(x: number, y: number, seed: number): number {
  let h = seed >>> 0;
  h = fmix(h ^ Math.imul(x | 0, 0x9e3779b1));
  h = fmix(h ^ Math.imul(y | 0, 0x85ebca77));
  return h;
}

export function hash3(x: number, y: number, z: number, seed: number): number {
  return fmix(hash2(x, y, seed) ^ Math.imul(z | 0, 0xc2b2ae3d));
}
